import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import api, { fileUrl, formatApiError } from "./api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FIELD_LABELS = {
  estimates: {
    email: "Email", phone: "Phone", address: "Project Address", services: "Services",
    budget: "Budget", start_date: "Preferred Start", description: "Details",
  },
  consultations: {
    email: "Email", phone: "Phone", date: "Date", time_slot: "Time Slot",
    consult_type: "Type", notes: "Notes",
  },
  callbacks: { phone: "Phone", best_time: "Best Time", notes: "Notes" },
  "payment-notices": {
    email: "Email", method: "Method", amount: "Amount", reference: "Invoice / Project Ref", note: "Note",
  },
  messages: { email: "Email", phone: "Phone", message: "Message" },
};

const STATUSES = ["new", "contacted", "scheduled", "in-progress", "completed", "closed"];

const STATUS_COLORS = {
  new: "text-[#D4AF37] border-[#D4AF37]",
  contacted: "text-sky-300 border-sky-400/50",
  scheduled: "text-violet-300 border-violet-400/50",
  "in-progress": "text-amber-300 border-amber-400/50",
  completed: "text-emerald-300 border-emerald-400/50",
  closed: "text-gray-400 border-gray-500",
};

export const SubmissionsPanel = ({ kind, onChanged }) => {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/submissions/${kind}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { setItems(null); load(); }, [kind]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${kind}/${id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success("Status updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveNote = async (id) => {
    try {
      await api.patch(`/admin/submissions/${kind}/${id}`, { admin_note: notes[id] ?? "" });
      toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await api.delete(`/admin/submissions/${kind}/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (items === null)
    return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37] mx-auto" /></div>;

  if (items.length === 0)
    return <p className="py-16 text-center text-gray-500 text-sm" data-testid={`${kind}-empty`}>No submissions yet.</p>;

  const labels = FIELD_LABELS[kind];

  return (
    <div className="space-y-4" data-testid={`${kind}-list`}>
      {items.map((item) => {
        const open = openId === item.id;
        const status = item.status || "new";
        return (
          <div key={item.id} data-testid={`${kind}-item-${item.id}`}
            className="bg-[#111111] border border-[#333333] hover:border-[#D4AF37]/50 transition-colors">
            <div className="flex flex-wrap items-center gap-3 md:gap-5 p-4 md:p-5">
              <button onClick={() => setOpenId(open ? null : item.id)} data-testid={`${kind}-toggle-${item.id}`}
                className="flex items-center gap-3 text-left flex-1 min-w-[200px]">
                {open ? <ChevronUp className="h-4 w-4 text-[#D4AF37]" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                <div>
                  <p className="text-white font-medium text-sm">{item.name}</p>
                  <p className="text-[11px] text-gray-500">
                    {item.created_at ? new Date(item.created_at).toLocaleString() : ""}
                  </p>
                </div>
              </button>
              <span className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 ${STATUS_COLORS[status]}`}
                data-testid={`${kind}-status-${item.id}`}>
                {status}
              </span>
              <Select value={status} onValueChange={(v) => setStatus(item.id, v)}>
                <SelectTrigger data-testid={`${kind}-status-select-${item.id}`}
                  className="w-36 h-9 bg-transparent border-[#333333] text-gray-300 text-xs rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-[#333333] text-white">
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`${kind}-status-option-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button onClick={() => remove(item.id)} data-testid={`${kind}-delete-${item.id}`}
                className="text-gray-500 hover:text-red-400 transition-colors p-2" aria-label="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {open && (
              <div className="border-t border-[#222222] p-4 md:p-5 grid md:grid-cols-2 gap-x-10 gap-y-3">
                {Object.entries(labels).map(([key, label]) => (
                  <div key={key}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</p>
                    <p className="text-sm text-gray-200 mt-0.5 whitespace-pre-wrap">
                      {Array.isArray(item[key]) ? item[key].join(", ") : item[key] || "—"}
                    </p>
                  </div>
                ))}
                {item.photos?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Photos</p>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {item.photos.map((f) => (
                        <a key={f} href={fileUrl(`/api/uploads/${f}`)} target="_blank" rel="noreferrer"
                          data-testid={`${kind}-photo-${f}`} className="text-xs text-[#D4AF37] hover:underline inline-flex items-center gap-1">
                          Photo <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1">Admin Note</p>
                  <div className="flex gap-3">
                    <textarea rows={2} value={notes[item.id] ?? item.admin_note ?? ""}
                      onChange={(e) => setNotes({ ...notes, [item.id]: e.target.value })}
                      data-testid={`${kind}-note-${item.id}`}
                      className="flex-1 bg-transparent border border-[#333333] focus:border-[#D4AF37] text-white text-sm p-2 outline-none resize-none" />
                    <button onClick={() => saveNote(item.id)} data-testid={`${kind}-save-note-${item.id}`}
                      className="self-end text-[11px] uppercase tracking-widest border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors px-4 py-2">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
